import { h, icon } from './dom.js';
import { load, save } from './store.js';

// The site as an app a visitor can install on a phone, tablet or computer
const listeners = new Set();
let prompt = (typeof window !== 'undefined' ? window.deferredPrompt : null) || null;
let installed = false;

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const ios = /iphone|ipad|ipod/i.test(ua) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox|Android/i.test(ua);
const isMacSafari = isSafari && !ios;
const isAndroid = /Android/i.test(ua);
const standalone = () =>
  typeof window !== 'undefined' &&
  (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: minimal-ui)').matches || navigator.standalone === true);

const changed = () => listeners.forEach((fn) => fn());
export const onInstallChange = (fn) => listeners.add(fn);

if (typeof window !== 'undefined') {
  window.addEventListener('pwa-prompt-ready', () => {
    prompt = window.deferredPrompt;
    changed();
  });

  window.addEventListener('pwa-installed', () => {
    prompt = null;
    installed = true;
    changed();
  });

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    prompt = event;
    window.deferredPrompt = event;
    changed();
  });

  window.addEventListener('appinstalled', () => {
    prompt = null;
    window.deferredPrompt = null;
    installed = true;
    changed();
  });
}

// Count site visits for install banner timing
function countVisits() {
  try {
    if (!sessionStorage.getItem('vs:counted')) {
      sessionStorage.setItem('vs:counted', '1');
      save('visits', Number(load('visits', 0)) + 1);
    }
  } catch {}
  return Number(load('visits', 0));
}
const visits = countVisits();

/**
 * Whether the install button should be visible.
 * Always true when PWA is enabled and site is not already running in standalone mode.
 */
export const canInstall = (design, preview = false) => design['pwa.enabled'] !== false && !installed && !standalone();

/** Registers the offline worker */
export function registerWorker({ mount, design }) {
  if (!('serviceWorker' in navigator) || window.top !== window) return;
  if (design['pwa.enabled'] === false) {
    navigator.serviceWorker
      .getRegistrations()
      .then((all) => all.filter((r) => r.scope.endsWith(mount)).forEach((r) => r.unregister()))
      .catch(() => {});
    return;
  }
  navigator.serviceWorker
    .register(mount + 'sw.js', { scope: mount, updateViaCache: 'none' })
    .then((reg) => {
      reg.update().catch(() => {});
    })
    .catch(() => {});
}

function steps(ctx, close) {
  const { text } = ctx;
  const siteName = ctx.site.name || 'ZXNITE';
  const tagline = ctx.design['brand.tagline'] || 'A-Z Drama Nights';

  let instructionTitle = 'Install from Address Bar';
  let instructionDesc = '';
  let badgeIcon = '🖥️';

  if (ios) {
    badgeIcon = '📱';
    instructionTitle = 'Install on iPhone / iPad';
    instructionDesc = '1. Tap the Share button (⎋) at the bottom of Safari.\n2. Scroll down and tap "Add to Home Screen" (⊞).';
  } else if (isMacSafari) {
    badgeIcon = '🍎';
    instructionTitle = 'Install on Mac Safari';
    instructionDesc = 'In the Safari top menu bar, choose File > "Add to Dock".';
  } else if (isAndroid) {
    badgeIcon = '🤖';
    instructionTitle = 'Install on Android';
    instructionDesc = 'Tap the menu (⋮) in Chrome, then tap "Install app" or "Add to Home screen".';
  } else {
    badgeIcon = '⚡';
    instructionTitle = 'Install via Address Bar (Above ⬆️)';
    instructionDesc = '1. Look at the top right of your browser address bar above ⬆️\n2. Click the Install icon (⊕ or 🖥️).\n3. Click "Install" in the browser confirmation.';
  }

  const iconImg = h('img', { src: '/icon-192.png', alt: siteName, class: 'install-dialog-icon' });
  const appMeta = h(
    'div',
    { class: 'install-dialog-meta' },
    h('h2', { id: 'install-title' }, siteName),
    h('p', { class: 'install-tagline' }, tagline),
    h('span', { class: 'install-chip' }, `${badgeIcon} Official Web App`)
  );
  const appPreview = h('div', { class: 'install-dialog-preview' }, iconImg, appMeta);

  const descP = h(
    'p',
    { class: 'install-dialog-desc' },
    text.fill(
      text.t('install_text') ||
        'Add {name} to your home screen or desktop. It opens full screen in one tap with offline playback support.',
      { name: siteName }
    )
  );

  const stepsBox = h(
    'div',
    { class: 'install-dialog-steps', id: 'install-steps-box' },
    h('strong', {}, instructionTitle),
    h('p', { style: 'white-space: pre-line; margin-top: 6px; line-height: 1.5;' }, instructionDesc)
  );

  const activePrompt = prompt || (typeof window !== 'undefined' ? window.deferredPrompt : null);

  const actionBtn = h(
    'button',
    {
      class: 'btn primary',
      type: 'button',
      on: {
        click: async () => {
          const p = prompt || (typeof window !== 'undefined' ? window.deferredPrompt : null);
          if (p) {
            prompt = null;
            if (typeof window !== 'undefined') window.deferredPrompt = null;
            changed();
            close();
            try {
              await p.prompt();
              const choice = await p.userChoice.catch(() => null);
              if (choice?.outcome === 'accepted') installed = true;
            } catch {}
            changed();
          } else {
            close();
          }
        },
      },
    },
    activePrompt ? 'Install Now' : 'Got it'
  );

  const closeBtn = activePrompt ? h('button', { class: 'btn', type: 'button', on: { click: close } }, text.t('install_close') || 'Not now') : null;

  const actions = h('div', { class: 'actions' }, actionBtn, closeBtn);

  const box = h(
    'div',
    { class: 'dialog install-dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'install-title' },
    appPreview,
    descP,
    stepsBox,
    actions
  );

  const back = h('div', { class: 'dialog-backdrop', on: { click: (e) => e.target === back && close() } }, box);
  back.addEventListener('keydown', (e) => e.key === 'Escape' && close());
  queueMicrotask(() => actionBtn.focus());
  return back;
}

export async function install(ctx) {
  const activePrompt = prompt || (typeof window !== 'undefined' ? window.deferredPrompt : null);
  if (activePrompt) {
    prompt = null;
    if (typeof window !== 'undefined') window.deferredPrompt = null;
    changed();
    try {
      await activePrompt.prompt();
      const choice = await activePrompt.userChoice.catch(() => null);
      if (choice?.outcome === 'accepted') {
        installed = true;
        changed();
        return;
      }
    } catch (e) {
      console.warn('Native prompt failed:', e);
    }
    changed();
    return;
  }
  const opener = document.activeElement;
  const close = () => {
    dialog.remove();
    opener?.focus?.();
  };
  const dialog = steps(ctx, close);
  document.body.append(dialog);
}

/** Render install links in sidebar, header, and footer */
export function installLinks(ctx) {
  for (const old of document.querySelectorAll('.install-link, .install-bar')) old.remove();
  const { design, text } = ctx;
  if (!canInstall(design, ctx.preview)) return;

  const where = design['pwa.install_link'] ?? 'both';
  const label = text.t('install_app') || 'Install app';
  const makeBtn = (extra) => h('button', { class: `install-link ${extra}`, type: 'button', on: { click: () => void install(ctx) } }, label);

  // 1. Sidebar menu
  const sideNav = document.querySelector('.side-nav');
  if (sideNav && (where === 'both' || where === 'sidebar')) {
    const sideBtn = h('button', { class: 'install-link in-side', type: 'button', on: { click: () => void install(ctx) } }, icon('download'), h('span', {}, label));
    sideNav.append(sideBtn);
  }

  // 2. Header
  if (where === 'both' || where === 'header') {
    const wrap = document.querySelector('.header .wrap');
    const toggle = document.querySelector('.header .menu-toggle');
    if (wrap) wrap.insertBefore(makeBtn('in-header'), toggle);
  }

  // 3. Footer
  if (where === 'both' || where === 'footer') {
    document.querySelector('.footer .wrap')?.append(h('div', { class: 'install-link in-footer' }, makeBtn('')));
  }

  // 4. Floating install bar for repeat visits
  const closedAt = Number(load('install-closed', 0));
  if (design['pwa.banner'] && !ctx.preview && visits >= 2 && Date.now() - closedAt > 30 * 86_400_000) {
    const siteName = ctx.site.name || 'ZXNITE';
    const bar = h(
      'div',
      { class: 'install-bar', role: 'region', 'aria-label': text.fill(text.t('install_title') || 'Install {name}', { name: siteName }) },
      h('p', {}, text.fill(text.t('install_title') || 'Install {name}', { name: siteName })),
      h(
        'div',
        { class: 'actions' },
        h('button', { class: 'btn primary', type: 'button', on: { click: () => void install(ctx) } }, label),
        h('button', { class: 'btn', type: 'button', on: { click: () => { save('install-closed', Date.now()); bar.remove(); } } }, text.t('install_close') || 'Not now')
      )
    );
    document.body.append(bar);
  }
}
